import { isAllowedLinkHost } from './allowedLinkDomains';

export type TextSegment = { kind: 'text'; text: string } | { kind: 'link'; text: string; href: string };

// Only bare http(s):// and www. URLs, so no other scheme (javascript:, data:) can become a link. A URL ends
// at `](`, so a markdown link whose text is itself a URL gives two working links rather than one broken one.
// It also ends at the zero-width characters that come along when copying from a web page or Word, which
// `\s` doesn't cover (it does cover U+FEFF).
const URL_CANDIDATE = /(?:https?:\/\/|www\.)(?:(?!\]\()[^\s<>"\u200B-\u200D\u2060])+/gi;

// A URL starts at a word boundary, so `xhttps://…`, `påhttps://…` and the `www.` in an email address or in
// `ftp://www.…` stay text.
const PART_OF_WORD = /[\p{L}\p{N}_@./-]/u;

// Sentence punctuation right after a URL ("Läs här: https://…/artikel.") is not part of it, nor is
// the backtick that closes a code span an agent pasted from an AI draft.
const TRAILING_PUNCTUATION = new Set(['.', ',', ':', ';', '!', '?', "'", '*', '_', '~', '`', '…', '“', '”', '’', '»']);

const OPENING_BRACKET: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

// One code point of an emoji: a pictograph, skin tone, flag letter or the tag characters of a flag like
// Scotland's, or the joiner and variation selector that build one.
const EMOJI_PART =
    /^[\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}\u{E0020}-\u{E007F}\p{Join_Control}\p{Variation_Selector}]$/u;

// A keycap emoji (1️⃣) is a plain digit, `#` or `*` followed by U+20E3, so the digit goes with it.
const KEYCAP = /[0-9#*]\uFE0F?\u20E3$/;

function trimTrailing(candidate: string): string {
    let url = candidate;
    // Kept up to date as the loop trims: counting the URL afresh on every step took 2 s on 20,000 `)`.
    const brackets: Record<string, number> = { '(': 0, ')': 0, '[': 0, ']': 0, '{': 0, '}': 0 };
    for (const char of url) {
        if (char in brackets) {
            brackets[char] += 1;
        }
    }
    while (url.length > 0) {
        // Read the last code point, not the last code unit, since an emoji is a surrogate pair. Test only
        // that one: a `[…]+$` regex here rescanned the URL on every step and took 28 s on a 4,000-character
        // message of emoji and full stops.
        const last = Array.from(url.slice(-2)).pop() ?? '';
        const keycap = KEYCAP.exec(url.slice(-3))?.[0];
        // A closing bracket stays only when it closes one opened inside the URL, as in a Wikipedia
        // `…/Moms_(skatt)` link, so "(se https://…)" leaves the parenthesis out.
        const opening = OPENING_BRACKET[last] as string | undefined;
        const unbalanced = opening !== undefined && brackets[last] > brackets[opening];
        if (keycap === undefined && !TRAILING_PUNCTUATION.has(last) && !unbalanced && !EMOJI_PART.test(last)) {
            break;
        }
        if (opening !== undefined) {
            brackets[last] -= 1;
        }
        url = url.slice(0, -(keycap ?? last).length);
    }
    return url;
}

function toHref(url: string): string | undefined {
    // Trimming can leave nothing past the prefix ("www..").
    if (!/^(?:https?:\/\/|www\.)./i.test(url)) {
        return undefined;
    }
    let parsed: URL;
    try {
        parsed = new URL(/^www\./i.test(url) ? `https://${url}` : url);
    } catch {
        return undefined;
    }
    // The host as the browser will resolve it, so `https://spiris.se@evil.example` is checked as evil.example.
    return isAllowedLinkHost(parsed.hostname) ? parsed.href : undefined;
}

/**
 * Splits plain text into text and link segments. Only URLs to our allowed link domains become
 * links, so a hijacked support account can only link to our own and the authorities' sites. Link
 * text is the URL exactly as written; everything else is returned untouched, with no markdown.
 */
export function linkifyText(text: string): TextSegment[] {
    const segments: TextSegment[] = [];
    let textStart = 0;
    for (const match of text.matchAll(URL_CANDIDATE)) {
        const start = match.index;
        if (start > 0 && PART_OF_WORD.test(text.charAt(start - 1))) {
            continue;
        }
        const url = trimTrailing(match[0]);
        const href = toHref(url);
        if (href === undefined) {
            continue;
        }
        if (start > textStart) {
            segments.push({ kind: 'text', text: text.slice(textStart, start) });
        }
        segments.push({ kind: 'link', text: url, href });
        textStart = start + url.length;
    }
    if (textStart < text.length) {
        segments.push({ kind: 'text', text: text.slice(textStart) });
    }
    return segments;
}
